/* ==========================================================================
   Siraat — Upgrade & Save + Gifts (siraat_upgrade_gifts block)

   Deferred, no dependencies. One instance per page; every handler is
   delegated on document/window and scoped to the block root
   ([data-sug-root]), so any number of blocks can coexist and the block
   keeps working after the pan size selector swaps the whole product
   section (outerHTML replacement) or the theme editor re-renders it.

   What it does
   1. Tier selection: aria-pressed on the tier buttons, .is-on on the card
      (which reveals the gifts panel), and the matching price summary /
      installments line. The choice is kept while the page is open; a new
      page load starts on the template's default tier.
   2. Add to cart: the block renders the product form with the theme's
      product_form_id, so its own button AND the theme's sticky bar submit
      it. The submit is intercepted here (window, capture phase: runs
      before cart-fast-add.js and the theme) and the selected tier's items
      are posted to /cart/add.js in one call. Then the theme's own events
      are dispatched (variant:add on the form, cart:change with
      baseEvent "variant:add" on <html>) so <cart-drawer> re-renders and
      opens and the header count syncs, exactly like a normal add.
   3. Variant change (products with real variants): follows variant:change
      from the theme's variant picker for tier 1 (price, compare-at,
      saving, availability and the id that gets added).
   ========================================================================== */
(function () {
  'use strict';
  if (window.__siraatUpgradeGifts) return;
  window.__siraatUpgradeGifts = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var memory = {};

  /* ---- helpers ----------------------------------------------------------- */
  function settings() { return (window.themeVariables && window.themeVariables.settings) || {}; }

  /* The choice is remembered only while the page is open (it survives a
     section re-render). A fresh page load always starts on the template's
     default tier, so landing on a pan URL shows that pan selected. */
  function readStored(id) { return memory[id] || null; }
  function store(id, tier) { memory[id] = tier; }

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  /* Minimal Shopify money formatter (same placeholders as shop.money_format). */
  function formatMoney(cents) {
    var format = settings().moneyFormat || '${{amount}}';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var key = match ? match[1] : 'amount';
    var value = (parseInt(cents, 10) || 0) / 100;
    function group(numStr, sep) { return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    var out;
    switch (key) {
      case 'amount_no_decimals': out = group(Math.round(value).toString(), ','); break;
      case 'amount_with_comma_separator': out = group(value.toFixed(2).split('.')[0], '.') + ',' + value.toFixed(2).split('.')[1]; break;
      case 'amount_no_decimals_with_comma_separator': out = group(Math.round(value).toString(), '.'); break;
      case 'amount_with_space_separator': out = group(value.toFixed(2).split('.')[0], ' ') + ',' + value.toFixed(2).split('.')[1]; break;
      case 'amount_no_decimals_with_space_separator': out = group(Math.round(value).toString(), ' '); break;
      default: out = group(value.toFixed(2).split('.')[0], ',') + '.' + value.toFixed(2).split('.')[1];
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  function showError(root, message) {
    var el = root.querySelector('.sug-error');
    if (!el) return;
    el.textContent = message || root.getAttribute('data-sug-error') || 'Something went wrong. Please try again.';
    el.hidden = false;
  }
  function hideError(root) {
    var el = root.querySelector('.sug-error');
    if (el) { el.hidden = true; el.textContent = ''; }
  }

  function stickyBar(root) {
    var formId = root.getAttribute('data-sug-form-id');
    return formId ? document.querySelector('product-sticky-add-to-cart[main-product-form="' + formId + '"]') : null;
  }

  /* ---- theme Price block sync -------------------------------------------- */
  /* The block has no big total of its own: the theme's Price block (price-list:
     sale-price, compare-at-price and the "Save" pill) shows the price of the
     selected tier and size. Values come pre-formatted from Liquid on the tier
     button (data-sug-price / -compare / -save), so the currency is always right. */
  function themePriceList(root) {
    var scope = root.closest('.product-info') || document;
    return scope.querySelector('[data-block-type="price"] price-list');
  }
  function setMoneyText(el, text) {
    var sr = el.querySelector('.sr-only');
    el.textContent = '';
    if (sr) el.appendChild(sr);
    el.appendChild(document.createTextNode(text));
  }
  function syncThemePrice(root) {
    var list = themePriceList(root);
    if (!list) return;
    var btn = root.querySelector('[data-sug-tier="' + root.getAttribute('data-sug-selected') + '"]');
    if (!btn) return;
    var price = btn.getAttribute('data-sug-price') || '';
    var compare = btn.getAttribute('data-sug-compare') || '';
    var save = btn.getAttribute('data-sug-save') || '';
    var vid = btn.getAttribute('data-sug-vid');
    var sale = list.querySelector('sale-price');
    if (sale && price) {
      setMoneyText(sale, price);
      if (vid) sale.setAttribute('data-variant-id', vid);
    }
    var was = list.querySelector('compare-at-price');
    if (was) { if (compare) setMoneyText(was, compare); was.hidden = !compare; }
    var pill = list.querySelector('.cst-sale-text');
    if (pill) {
      var prefix = pill.getAttribute('data-sug-prefix');
      if (prefix === null) { prefix = (pill.textContent.trim().split(/\s+/)[0] || ''); pill.setAttribute('data-sug-prefix', prefix); }
      if (save) pill.textContent = prefix + ' ' + save;
      pill.hidden = !save;
    }
  }

  /* The tier that sells this page's own product ("1" unless tier 1 is another product, e.g. the pan on a set's page);
     the sticky bar shows that product's price, so it is only shown while that tier is chosen. */
  function ownTier(root) {
    var own = root.getAttribute('data-sug-own-tier');
    return own === null ? '1' : own;
  }

  /* ---- tier selection ---------------------------------------------------- */
  function select(root, tier, silent) {
    var target = root.querySelector('[data-sug-tier="' + tier + '"]');
    if (!target) return false;
    each(root.querySelectorAll('[data-sug-tier]'), function (btn) {
      var on = btn === target;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var card = btn.closest('[data-sug-card]');
      if (card) card.classList.toggle('is-on', on);
    });
    /* Accordion: a set's piece list closes when another option is chosen */
    each(root.querySelectorAll('[data-sug-more]'), function (more) {
      var card = more.closest('[data-sug-card]');
      if (card && card.getAttribute('data-sug-card') !== tier && more.getAttribute('aria-expanded') === 'true') {
        setSetOpen(more, false);
      }
    });
    each(root.querySelectorAll('[data-sug-summary]'), function (el) { el.hidden = el.getAttribute('data-sug-summary') !== tier; });
    each(root.querySelectorAll('[data-sug-terms]'), function (el) { el.hidden = el.getAttribute('data-sug-terms') !== tier; });
    root.setAttribute('data-sug-selected', tier);
    syncPanels(root);
    hideError(root);
    /* The sticky bar shows the single-pan price; hide it while a set tier is active
       so the shopper never taps a "$134" button that adds a set. */
    var sticky = stickyBar(root);
    if (sticky) sticky.hidden = tier !== ownTier(root);
    if (!silent) store(root.getAttribute('data-sug-id'), tier);
    syncThemePrice(root);
    syncExternal(root);
    return true;
  }

  /* ---- standalone mode: the theme's own Add to cart button --------------- */
  /* With "Use the theme's Add to cart button" on, the block renders no form of
     its own. The choice goes into the theme's product form (Buy buttons block,
     same form id): its variant id, plus a quantity field when above 1. The
     theme's button, cart-fast-add.js and the sticky bar then add it as usual.
     A tier made of several products cannot travel as one id; that tier alone is
     added by this script (see the submit listener). */
  function externalForm(root) {
    if (!root.hasAttribute('data-sug-external')) return null;
    var id = root.getAttribute('data-sug-form-id');
    var form = id ? document.getElementById(id) : null;
    return form && form.tagName === 'FORM' && !root.contains(form) ? form : null;
  }
  function tierItems(root) {
    var btn = root.querySelector('[data-sug-tier="' + root.getAttribute('data-sug-selected') + '"]');
    var spec = btn ? (btn.getAttribute('data-sug-items') || '') : '';
    return spec.split(',').map(function (part) {
      var bits = part.trim().split(':');
      return { id: parseInt(bits[0], 10), quantity: parseInt(bits[1] || '1', 10) || 1 };
    }).filter(function (item) { return item.id > 0; });
  }
  function isShown(root) { return root.getClientRects().length > 0; }
  function syncExternal(root) {
    var form = externalForm(root);
    if (!form || !form.elements.id) return;
    /* Hidden block (e.g. an on-site edit for the control group): the theme form keeps its own product */
    if (!isShown(root)) return;
    var items = tierItems(root);
    if (items.length !== 1) return;
    form.elements.id.value = items[0].id;
    var qty = form.querySelector('input[data-sug-qty]');
    if (items[0].quantity > 1 && (qty || !form.elements.quantity)) {
      if (!qty) {
        qty = document.createElement('input');
        qty.type = 'hidden';
        qty.name = 'quantity';
        qty.setAttribute('data-sug-qty', '');
        form.appendChild(qty);
      }
      qty.value = items[0].quantity;
    } else if (qty) {
      qty.parentNode.removeChild(qty);
    }
  }

  /* Collapsible panels animate open/closed with CSS (grid rows 0fr -> 1fr);
     closed ones are inert so their buttons cannot be tabbed to. */
  function syncPanels(root) {
    each(root.querySelectorAll('[data-sug-card]'), function (card) {
      var panel = card.querySelector('.sug-card__panel');
      if (panel) panel.inert = !card.classList.contains('is-on');
    });
  }
  function setSetOpen(more, open) {
    more.setAttribute('aria-expanded', open ? 'true' : 'false');
    var panel = document.getElementById(more.getAttribute('aria-controls'));
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.inert = !open;
  }

  function init(root) {
    if (root.__sugReady) return;
    root.__sugReady = true;
    syncPanels(root);
    var stored = readStored(root.getAttribute('data-sug-id'));
    if (stored && stored !== root.getAttribute('data-sug-selected')) select(root, stored, true);
    else if (root.getAttribute('data-sug-selected') !== ownTier(root)) { var sticky = stickyBar(root); if (sticky) sticky.hidden = true; }
    syncThemePrice(root);
    syncExternal(root);
  }
  function initAll() { each(document.querySelectorAll('[data-sug-root]'), init); }

  /* The whole card is the tap target (not just its top row). Controls inside a
     card (size buttons, "View all", links) keep their own behaviour. */
  document.addEventListener('click', function (event) {
    var card = event.target.closest && event.target.closest('[data-sug-card]');
    if (!card) return;
    if (event.target.closest('[data-sug-pill], [data-sug-more], [data-sug-guide], a, select, input, label')) return;
    var root = card.closest('[data-sug-root]');
    var btn = card.querySelector('[data-sug-tier]');
    if (!root || !btn || btn.hasAttribute('data-sug-soldout')) return;
    select(root, btn.getAttribute('data-sug-tier'));
  });

  /* ---- cart snapshot (fast add) ------------------------------------------ */
  /* Same approach as the theme's cart-fast-add.js: keep a recent cart.js
     snapshot, so after /cart/add.js the drawer can render straight from that
     response (its section HTML is already final) instead of waiting for a
     second, sequential cart.js round trip. cart.js then trues up in the
     background. */
  var cachedCart = null;
  var warming = false;
  document.addEventListener('cart:change', function (event) {
    var c = event.detail && event.detail.cart;
    if (c && c.items) cachedCart = c;
  });
  function warmCart() {
    if (cachedCart || warming || !window.fetch) return;
    warming = true;
    fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { if (!cachedCart && c && c.items) cachedCart = c; })
      .catch(function () {})
      .then(function () { warming = false; });
  }
  function synthesizeCart(cached, added) {
    var kept = (cached.items || []).filter(function (it) {
      return !added.some(function (a) { return a.key === it.key; });
    });
    var items = added.concat(kept);
    var count = 0;
    items.forEach(function (it) { count += it.quantity || 0; });
    var cart = {};
    for (var k in cached) { if (Object.prototype.hasOwnProperty.call(cached, k)) cart[k] = cached[k]; }
    cart.items = items;
    cart.item_count = count;
    return cart;
  }
  /* Warm the snapshot early: when idle after load, and again the moment a
     shopper touches the block (a tier or the button), well before the tap lands. */
  function scheduleWarm() {
    if (window.requestIdleCallback) window.requestIdleCallback(warmCart, { timeout: 2500 });
    else setTimeout(warmCart, 1200);
  }
  if (document.readyState === 'complete') scheduleWarm();
  else window.addEventListener('load', scheduleWarm, { once: true });
  document.addEventListener('pointerdown', function (event) {
    if (event.target.closest && event.target.closest('[data-sug-root]')) warmCart();
  }, { passive: true });

  /* ---- add to cart ------------------------------------------------------- */
  function selectedItems(root, form) {
    var tier = root.getAttribute('data-sug-selected');
    var btn = root.querySelector('[data-sug-tier="' + tier + '"]');
    var spec = btn ? (btn.getAttribute('data-sug-items') || '') : '';
    if (tier === '1' && form.elements.id && form.elements.id.value) spec = form.elements.id.value + ':1';
    return spec.split(',').map(function (part) {
      var bits = part.trim().split(':');
      return { id: parseInt(bits[0], 10), quantity: parseInt(bits[1] || '1', 10) || 1 };
    }).filter(function (item) { return item.id > 0; });
  }

  function submitButtons(form) {
    var list = [];
    each(form.elements, function (el) { if (el.type === 'submit') list.push(el); });
    if (form.id) each(document.querySelectorAll('button[form="' + form.id + '"], input[type="submit"][form="' + form.id + '"]'), function (el) { if (list.indexOf(el) === -1) list.push(el); });
    return list;
  }

  /* ---- instant feedback in the drawer ------------------------------------ */
  /* The drawer opens at the tap, but its real content only exists once
     /cart/add.js has rendered it. Until then it showed the empty-cart state
     (or the old cart) with nothing happening. Now the chosen item appears at
     the top of the drawer immediately, with a thin progress bar; the theme's
     cart:change render replaces the drawer's children, which removes it. */
  function showPending(root, drawer) {
    if (!drawer) return null;
    var anchor = drawer.querySelector('.cart-drawer__empty-state') || drawer.querySelector('.cart-drawer__filled-state');
    if (!anchor || !anchor.parentNode) return null;
    var btn = root.querySelector('[data-sug-tier="' + root.getAttribute('data-sug-selected') + '"]');
    if (!btn) return null;
    var img = btn.querySelector('.sug-tier__thumb img');
    var label = btn.querySelector('.sug-tier__label') || btn.querySelector('.sug-tier__name');
    var sub = btn.querySelector('.sug-tier__sub');

    var el = document.createElement('div');
    el.className = 'sug-pending';
    el.setAttribute('role', 'status');
    var thumb = document.createElement('span');
    thumb.className = 'sug-pending__thumb';
    if (img) {
      var copy = document.createElement('img');
      copy.src = img.currentSrc || img.src;
      copy.alt = '';
      thumb.appendChild(copy);
    }
    var text = document.createElement('span');
    text.className = 'sug-pending__text';
    var title = document.createElement('b');
    title.textContent = label ? label.textContent.trim() : '';
    text.appendChild(title);
    if (sub && sub.textContent.trim()) {
      var s2 = document.createElement('small');
      s2.textContent = sub.textContent.trim();
      text.appendChild(s2);
    }
    var price = document.createElement('span');
    price.className = 'sug-pending__price';
    price.textContent = btn.getAttribute('data-sug-price') || '';
    var bar = document.createElement('span');
    bar.className = 'sug-pending__bar';
    el.appendChild(thumb); el.appendChild(text); el.appendChild(price); el.appendChild(bar);

    var empty = drawer.querySelector('.cart-drawer__empty-state');
    var emptyWasHidden = empty ? empty.hidden : true;
    if (empty) empty.hidden = true;
    anchor.parentNode.insertBefore(el, anchor);
    return {
      remove: function () {
        if (el.parentNode) el.parentNode.removeChild(el);
        if (empty && empty.isConnected) empty.hidden = emptyWasHidden;
      }
    };
  }

  function addSelected(root, form) {
    if (root.__sugBusy) return;
    var items = selectedItems(root, form);
    if (!items.length) { showError(root); return; }

    var buttons = submitButtons(form);
    var cfg = settings();
    var drawer = document.getElementById('cart-drawer');
    var openedByUs = false;

    root.__sugBusy = true;
    hideError(root);
    buttons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });

    /* Instant: the item shows in the drawer first, then the drawer opens. No
       theme:loading:start here: the theme answers it with a full-screen dimming
       overlay that would sit on top of the item. */
    var pending = null;
    if (cfg.cartType === 'drawer' && drawer) {
      pending = showPending(root, drawer);
      if (typeof drawer.show === 'function' && !drawer.hasAttribute('open')) {
        try { drawer.show(); openedByUs = true; } catch (e) {}
      }
    }

    var bundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: bundle } }));

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items, sections: Array.from(bundle).join(',') })
    })
      .then(function (response) { return response.json().then(function (json) { return { ok: response.ok, json: json }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error((res.json && (res.json.description || res.json.message)) || '');
        if (cfg.cartType === 'page' || cfg.pageType === 'cart') { window.location.href = ROOT + 'cart'; return; }
        var added = res.json.items ? res.json.items : [res.json];
        var announce = function (cart) {
          cart.sections = res.json.sections;
          form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added, cart: cart } }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', items: added, cart: cart } }));
        };
        if (cachedCart && cachedCart.items) {
          /* Fast path: one round trip. The drawer re-renders from the add.js sections now. */
          announce(synthesizeCart(cachedCart, added));
          fetch(ROOT + 'cart.js').then(function (r) { return r.json(); })
            .then(function (c) { if (c && c.items) cachedCart = c; }).catch(function () {});
          return;
        }
        return fetch(ROOT + 'cart.js').then(function (r) { return r.json(); }).then(announce);
      })
      .catch(function (err) {
        if (pending) pending.remove();
        showError(root, err && err.message ? err.message : '');
        if (openedByUs && drawer && typeof drawer.hide === 'function') { try { drawer.hide(); } catch (e) {} }
      })
      .then(function () {
        root.__sugBusy = false;
        buttons.forEach(function (b) { b.removeAttribute('aria-busy'); });
        /* Normally the drawer re-render already removed it; this covers a
           cart-page redirect or a render that failed. */
        if (pending) setTimeout(function () { pending.remove(); }, 0);
      });
  }

  /* Capture on window: runs before the document-level capture listeners of
     cart-fast-add.js and before the theme's <product-form>, for our form only. */
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-sug-form')) return;
    var root = form.closest('[data-sug-root]');
    if (!root) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addSelected(root, form);
  }, true);

  /* Standalone mode, multi-product tier: the theme form can carry one id only */
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.id || form.hasAttribute('data-sug-form')) return;
    each(document.querySelectorAll('[data-sug-root][data-sug-external]'), function (root) {
      if (event.defaultPrevented || externalForm(root) !== form || !isShown(root)) return;
      if (tierItems(root).length < 2) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addSelected(root, form);
    });
  }, true);

  /* ---- Okura-style size dropdown in the Single Pan card -------------------- */
  /* Each option carries its size product's variant id and server-formatted prices
     (right currency per market), so switching is instant: no reload, no fetch. */
  /* The size control is a row of buttons (data-sug-pill); a <select> still works for older markup. */
  function applySize(root, control) {
    var opt = control.tagName === 'SELECT' ? control.options[control.selectedIndex] : control;
    if (!opt) return;
    var tier1 = root.querySelector('[data-sug-tier="1"]');
    var form = root.querySelector('[data-sug-form]');
    if (tier1) {
      tier1.setAttribute('data-sug-items', opt.value + ':1');
      tier1.setAttribute('data-sug-vid', opt.value);
      tier1.setAttribute('data-sug-price', opt.getAttribute('data-price') || '');
      tier1.setAttribute('data-sug-compare', opt.getAttribute('data-compare') || '');
      tier1.setAttribute('data-sug-save', opt.getAttribute('data-save') || '');
    }
    if (form && form.elements.id) form.elements.id.value = opt.value;
    var price = opt.getAttribute('data-price') || '';
    var compare = opt.getAttribute('data-compare') || '';
    var save = opt.getAttribute('data-save') || '';
    each(root.querySelectorAll('[data-sug-t1-price]'), function (el) { el.textContent = price; });
    each(root.querySelectorAll('[data-sug-t1-compare]'), function (el) { el.textContent = compare; el.hidden = !compare; });
    each(root.querySelectorAll('[data-sug-t1-save]'), function (el) { el.hidden = !save; });
    each(root.querySelectorAll('[data-sug-t1-save-amount]'), function (el) { el.textContent = save; });
    var sub = root.querySelector('[data-sug-t1-sub]');
    if (sub) {
      var prefix = sub.getAttribute('data-sug-t1-prefix');
      if (prefix === null) { prefix = sub.getAttribute('data-prefix') || ''; sub.setAttribute('data-sug-t1-prefix', prefix); }
      sub.textContent = (prefix + ' ' + (opt.getAttribute('data-name') || '')).trim();
    }
    hideError(root);
    syncThemePrice(root);
    syncExternal(root);
  }

  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches('[data-sug-size]')) return;
    var root = select.closest('[data-sug-root]');
    if (root) applySize(root, select);
  });

  document.addEventListener('click', function (event) {
    var pill = event.target.closest && event.target.closest('[data-sug-pill]');
    if (!pill || pill.disabled) return;
    var root = pill.closest('[data-sug-root]');
    if (!root) return;
    each(root.querySelectorAll('[data-sug-pill]'), function (p) { p.setAttribute('aria-checked', p === pill ? 'true' : 'false'); });
    applySize(root, pill);
    if (pill.hasAttribute('data-sug-vpill')) selectThemeVariant(root, pill);
  });

  /* Variant buttons (real variants): pick the same value in the theme's variant picker, so it loads the
     variant's photos, URL and form id and fires variant:change, which the block follows (below). */
  function selectThemeVariant(root, pill) {
    var formId = root.getAttribute('data-sug-form-id');
    var value = pill.getAttribute('data-option-value');
    if (!formId || value === null) return;
    var inputs = document.querySelectorAll('input[data-option-position="1"][form="' + formId + '"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.value !== value || root.contains(input)) continue;
      if (!input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
  }

  /* ---- sets: "View all N pieces" opens the set photo + list and selects the set */
  document.addEventListener('click', function (event) {
    var more = event.target.closest && event.target.closest('[data-sug-more]');
    if (!more) return;
    var root = more.closest('[data-sug-root]');
    var card = more.closest('[data-sug-card]');
    if (!root || !card) return;
    setSetOpen(more, more.getAttribute('aria-expanded') !== 'true');
    var btn = card.querySelector('[data-sug-tier]');
    if (btn && !btn.hasAttribute('data-sug-soldout')) select(root, btn.getAttribute('data-sug-tier'));
  });

  /* ---- Single pan: "Size guide" opens the theme's size guide popup --------- */
  var guideOpen = null;
  var guideTrigger = null;

  function closeGuide() {
    if (!guideOpen) return;
    guideOpen.classList.remove('is-open');
    document.body.style.overflow = '';
    guideOpen = null;
    if (guideTrigger) guideTrigger.focus({ preventScroll: true });
    guideTrigger = null;
  }

  function openGuide(root, trigger) {
    var overlay = root._sugGuide;
    if (!overlay) {
      var tpl = root.querySelector('template[data-sug-guide-tpl]');
      if (!tpl) return;
      var frag = tpl.content.cloneNode(true);
      // Its own script and trigger stay out; ids go so it never collides with a Custom Size Variant block on the page
      each(frag.querySelectorAll('script, .sg-trigger'), function (n) { n.parentNode.removeChild(n); });
      each(frag.querySelectorAll('[id]'), function (n) { n.removeAttribute('id'); });
      overlay = frag.querySelector('.sg-overlay');
      if (!overlay) return;
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      document.body.appendChild(frag);
      overlay.addEventListener('click', function (event) {
        if (event.target === overlay || event.target.closest('.sg-close')) closeGuide();
      });
      void overlay.offsetWidth; // start the fade from the closed state
      root._sugGuide = overlay;
    }
    guideOpen = overlay;
    guideTrigger = trigger;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var close = overlay.querySelector('.sg-close');
    // Focus once the overlay is visible (it fades in from visibility: hidden)
    if (close) setTimeout(function () { if (guideOpen === overlay) close.focus({ preventScroll: true }); }, 60);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-sug-guide]');
    if (!trigger) return;
    var root = trigger.closest('[data-sug-root]');
    if (!root) return;
    event.preventDefault();
    openGuide(root, trigger);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeGuide();
  });

  /* ---- follow the theme's variant picker (tier 1) ------------------------- */
  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    var form = event.target;
    if (!variant || !form || !form.id) return;
    each(document.querySelectorAll('[data-sug-root][data-sug-form-id="' + form.id + '"]'), function (root) {
      var tier1 = root.querySelector('[data-sug-tier="1"]');
      if (!tier1 || ownTier(root) !== '1') return;
      tier1.setAttribute('data-sug-items', variant.id + ':1');
      each(root.querySelectorAll('[data-sug-vpill]'), function (p) { p.setAttribute('aria-checked', String(p.value) === String(variant.id) ? 'true' : 'false'); });
      var price = variant.price, compare = variant.compare_at_price || 0, save = compare > price ? compare - price : 0;
      tier1.setAttribute('data-sug-vid', variant.id);
      tier1.setAttribute('data-sug-price', formatMoney(price));
      tier1.setAttribute('data-sug-compare', save ? formatMoney(compare) : '');
      tier1.setAttribute('data-sug-save', save ? formatMoney(save) : '');
      each(root.querySelectorAll('[data-sug-t1-price]'), function (el) { el.textContent = formatMoney(price); });
      each(root.querySelectorAll('[data-sug-t1-compare]'), function (el) { el.textContent = save ? formatMoney(compare) : ''; el.hidden = !save; });
      each(root.querySelectorAll('[data-sug-t1-save]'), function (el) { el.hidden = !save; });
      each(root.querySelectorAll('[data-sug-t1-save-amount]'), function (el) { el.textContent = save ? formatMoney(save) : ''; });
      var sub = root.querySelector('[data-sug-t1-sub]');
      if (sub && variant.title && variant.title !== 'Default Title') {
        var prefix = sub.getAttribute('data-sug-t1-prefix');
        if (prefix === null) { prefix = sub.getAttribute('data-prefix') || ''; sub.setAttribute('data-sug-t1-prefix', prefix); }
        sub.textContent = (prefix + ' ' + variant.title).trim();
      }
      var summary = root.querySelector('[data-sug-summary="1"] .sug-cta');
      if (summary) { summary.disabled = !variant.available; }
      if (variant.available) tier1.removeAttribute('data-sug-soldout'); else tier1.setAttribute('data-sug-soldout', 'true');
    });
  });

  /* ---- (re)initialise: initial load, section swaps, theme editor ----------- */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);

  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute('data-sug-root') || node.querySelector('[data-sug-root]')) { initAll(); return; }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
